<template>
  <div>
    <a-drawer
      :title="$t('factures.detailTitle')"
      :width="920"
      :visible="visible"
      destroy-on-close
      @close="$emit('close')"
    >
      <template v-if="depense">
        <a-descriptions :column="2" bordered size="small" class="mb-3">
          <a-descriptions-item :label="$t('factures.tableLabel')" :span="2">
            {{ depense.label || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.invoiceNumber')">
            {{ depense.invoiceNumber || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('factures.reference')">
            {{ depense.reference || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.expenseMode')">
            {{
              depense.expenseMode === "declaree"
                ? $t("decaissement.declaree")
                : $t("decaissement.normal")
            }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.invoiceDate')">
            {{ formatDate(depense.date) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.depenseCreatedAt')">
            {{ formatDate(depense.entryDate || depense.createdAt) }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="depense.beneficiaryType"
            :label="$t('decaissement.beneficiaryType')"
          >
            <beneficiary-type-display :type="depense.beneficiaryType" />
          </a-descriptions-item>
          <a-descriptions-item :label="$t('decaissement.beneficiaryName')">
            {{ depense._beneficiaryName || "—" }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.invoiceStatus')">
            <a-tag :color="invoiceStatusColor(depense.invoiceStatus)">
              {{ invoiceStatusLabel(depense.invoiceStatus) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.schedulingStatus')">
            <a-tag
              :color="
                depense.schedulingStatus === 'scheduled' ? 'green' : 'orange'
              "
            >
              {{
                depense.schedulingStatus === "scheduled"
                  ? $t("fournisseur.scheduled")
                  : $t("fournisseur.notScheduled")
              }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.totalAmount')">
            {{ (depense._totalAmount || 0).toFixed(3) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.totalPaid')">
            {{ (depense._totalPaid || 0).toFixed(3) }}
          </a-descriptions-item>
          <a-descriptions-item :label="$t('fournisseur.remaining')" :span="2">
            <strong>{{ (depense._remaining || 0).toFixed(3) }}</strong>
          </a-descriptions-item>
          <a-descriptions-item
            v-if="levelDisplayLabels.length"
            :label="$t('decaissement.levels')"
            :span="2"
          >
            {{ levelDisplayLabels.join(", ") }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="classRoomDisplayLines.length"
            :label="$t('decaissement.classRoomAllocations')"
            :span="2"
          >
            <ul class="mb-0 pl-3 activity-allocation-detail-list">
              <li v-for="(line, i) in classRoomDisplayLines" :key="'cr-' + i">
                {{ line.label }} — {{ line.percent.toFixed(2) }}%
              </li>
            </ul>
          </a-descriptions-item>
          <a-descriptions-item
            v-if="activityDisplayLines.length"
            :label="$t('decaissement.activityAllocations')"
            :span="2"
          >
            <ul class="mb-0 pl-3 activity-allocation-detail-list">
              <li v-for="(line, i) in activityDisplayLines" :key="i">
                {{ line.label }} — {{ line.percent.toFixed(2) }}%
              </li>
            </ul>
          </a-descriptions-item>
          <template
            v-if="
              depense.expenseMode === 'declaree' &&
              depense.beneficiaryType === 'provider'
            "
          >
            <a-descriptions-item :label="$t('decaissement.amountTTC')">
              {{
                depense.amountTTCGross != null
                  ? depense.amountTTCGross.toFixed(3)
                  : "—"
              }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('decaissement.amountTVA')">
              {{
                depense.amountTVA != null ? depense.amountTVA.toFixed(3) : "—"
              }}
            </a-descriptions-item>
            <a-descriptions-item
              :label="$t('decaissement.amountAfterRetention')"
              :span="2"
            >
              {{
                depense.amountAfterRetention != null
                  ? depense.amountAfterRetention.toFixed(3)
                  : "—"
              }}
            </a-descriptions-item>
          </template>
          <a-descriptions-item
            :label="$t('decaissement.description')"
            :span="2"
          >
            {{ depense.description || "—" }}
          </a-descriptions-item>
          <a-descriptions-item
            v-if="planComptableTags.length"
            :label="$t('comptabilite.planComptable')"
            :span="2"
          >
            <a-tag
              v-for="tag in planComptableTags"
              :key="tag.key"
              color="geekblue"
              class="mr-2 mb-1"
            >
              {{ tag.label }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item :label="$t('factures.attachments')" :span="2">
            <span
              v-if="!depense.attachmentKeys || !depense.attachmentKeys.length"
            >
              {{ $t("factures.noAttachments") }}
            </span>
            <template v-else>
              <div class="mb-2">
                {{
                  $t("factures.attachmentCount", {
                    n: depense.attachmentKeys.length,
                  })
                }}
              </div>
              <div class="attachment-grid">
                <div
                  v-for="(key, idx) in depense.attachmentKeys"
                  :key="`${key}-${idx}`"
                  class="attachment-item"
                >
                  <div class="attachment-main">
                    <a-icon type="paper-clip" class="attachment-icon" />
                    <span
                      class="attachment-name"
                      :title="attachmentDisplayName(key)"
                    >
                      {{ attachmentDisplayName(key) }}
                    </span>
                  </div>
                  <a
                    :href="resourceUrl(key)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="attachment-action"
                  >
                    {{ $t("action.voir") }}
                  </a>
                </div>
              </div>
            </template>
          </a-descriptions-item>
        </a-descriptions>

        <template
          v-if="showCapexDepreciationDetail"
        >
          <a-divider orientation="left">{{
            $t("decaissement.capexSectionTitle")
          }}</a-divider>
          <a-descriptions bordered size="small" :column="2" class="mb-3">
            <a-descriptions-item :label="$t('decaissement.capexInvoiceDate')">
              {{ formatDate(depense.capexDepreciation.invoiceDate) }}
            </a-descriptions-item>
            <a-descriptions-item :label="$t('decaissement.capexDepreciationPeriodMonths')">
              {{ capexDepreciationMonthsDisplay }}
            </a-descriptions-item>
          </a-descriptions>
          <div class="text-muted small mb-2">
            {{ $t("decaissement.capexSchedulePreview") }}
          </div>
          <a-table
            :data-source="depense.capexDepreciation.schedule"
            :pagination="false"
            size="small"
            row-key="schoolYearLabel"
            class="mb-3"
          >
            <a-table-column
              :title="$t('decaissement.capexSchoolYearColumn')"
              data-index="schoolYearLabel"
              key="csy"
            />
            <a-table-column
              :title="$t('decaissement.capexMonthsColumn')"
              data-index="months"
              key="cmo"
            />
            <a-table-column
              :title="$t('decaissement.capexAmountColumn')"
              data-index="amount"
              key="camt"
            >
              <template slot-scope="text">
                <span class="amount-cell">{{
                  text != null ? Number(text).toFixed(3) : "—"
                }}</span>
              </template>
            </a-table-column>
          </a-table>
        </template>

        <a-divider />
        <div class="mb-3">
          <a-space wrap>
            <router-link
              v-if="providerId"
              :to="'/decaissement/fournisseur/' + providerId"
            >
              <a-button type="primary" icon="user">
                {{ $t("factures.viewSupplier") }}
              </a-button>
            </router-link>
            <router-link :to="{ path: '/decaissement/saisie-paiement' }">
              <a-button icon="credit-card">{{
                $t("factures.goToPaymentEntry")
              }}</a-button>
            </router-link>
          </a-space>
        </div>

        <depense-schedules-panel
          v-if="showSchedulesSection"
          :depense="depense"
          @refresh="$emit('refresh')"
        />
      </template>
    </a-drawer>
  </div>
</template>

<script>
import moment from "moment";
import { mapState } from "vuex";
import DepenseSchedulesPanel from "../DepenseSchedulesPanel.vue";
import BeneficiaryTypeDisplay from "../../../components/BeneficiaryTypeDisplay.vue";
import { getLevels } from "@/config/LevelConstants";

export default {
  name: "InvoiceDetailDrawer",
  components: { DepenseSchedulesPanel, BeneficiaryTypeDisplay },
  props: {
    visible: { type: Boolean, default: false },
    depense: { type: Object, default: null },
  },
  computed: {
    ...mapState(["settings", "user"]),
    planComptableTags() {
      const d = this.depense;
      if (!d) return [];
      const tags = [];
      const push = (key, prefix, code) => {
        if (!code) return;
        tags.push({ key, label: `${this.$t(prefix)} ${code}` });
      };
      const cat = d.category;
      if (cat && typeof cat === "object" && cat.accountingClass) {
        push("cat", "comptabilite.category", cat.accountingClass);
      }
      const sub = d.subCategory;
      if (sub && typeof sub === "object" && sub.accountingClass) {
        push("sub", "comptabilite.subCategory", sub.accountingClass);
      }
      const prov = d.provider;
      if (prov && typeof prov === "object" && prov.accountingClass) {
        push("provider", "factures.beneficiaryProvider", prov.accountingClass);
      }
      const comp = d.company;
      if (comp && typeof comp === "object" && comp.accountingClass) {
        push("company", "factures.beneficiaryCompany", comp.accountingClass);
      }
      const deb = d.debiteur;
      if (deb && typeof deb === "object" && deb.accountingClass) {
        push("deb", "factures.beneficiaryDebiteur", deb.accountingClass);
      }
      const assoc = d.associate;
      if (assoc && typeof assoc === "object" && assoc.accountingClass) {
        push("assoc", "factures.beneficiaryAssociate", assoc.accountingClass);
      }
      return tags;
    },
    countryCode() {
      return this.activeBuilding?.countryCode || "TN";
    },
    activeBuilding() {
      return (this.user?.building || []).find(
        (b) => b.dbName === this.settings?.activeBuilding
      );
    },
    schoolType() {
      return this.activeBuilding?.type || [];
    },
    levelDisplayLabels() {
      const d = this.depense;
      if (!d || !d.levels || !d.levels.length) return [];
      const schoolLevels = getLevels(
        this.$t.bind(this),
        this.schoolType,
        this.countryCode
      );
      return d.levels.map(
        (level) => schoolLevels[String(level)] || String(level)
      );
    },
    activityDisplayLines() {
      const d = this.depense;
      if (!d) return [];
      const rows = [];
      if (d.activityAllocations && d.activityAllocations.length) {
        for (const a of d.activityAllocations) {
          const act = a.activity;
          const label =
            typeof act === "object" && act != null
              ? act.designation || act.name || "—"
              : "—";
          rows.push({ label, percent: Number(a.percent) || 0 });
        }
        return rows;
      }
      if (d.linkedActivity) {
        const act = d.linkedActivity;
        const label =
          typeof act === "object" && act != null
            ? act.designation || act.name || "—"
            : "—";
        return [{ label, percent: 100 }];
      }
      return [];
    },
    classRoomDisplayLines() {
      const d = this.depense;
      if (!d) return [];
      const rows = [];
      if (d.classRoomAllocations && d.classRoomAllocations.length) {
        for (const a of d.classRoomAllocations) {
          const cr = a.classRoom;
          const label =
            typeof cr === "object" && cr != null ? cr.name || "—" : "—";
          rows.push({ label, percent: Number(a.percent) || 0 });
        }
        return rows;
      }
      if (d.classRoom && typeof d.classRoom === "object") {
        return [{ label: d.classRoom.name || "—", percent: 100 }];
      }
      return [];
    },
    showSchedulesSection() {
      const d = this.depense;
      return (
        d && d.expenseMode === "declaree" && d.beneficiaryType === "provider"
      );
    },
    showCapexDepreciationDetail() {
      const d = this.depense;
      if (!d || !d.capexDepreciation || !d.capexDepreciation.schedule || !d.capexDepreciation.schedule.length) {
        return false;
      }
      return (
        (d.expenseMode === "declaree" || d.type === "declaree") &&
        d.beneficiaryType === "provider"
      );
    },
    capexDepreciationMonthsDisplay() {
      const cd = this.depense && this.depense.capexDepreciation;
      if (!cd) return "—";
      if (cd.depreciationPeriodMonths != null && cd.depreciationPeriodMonths !== "") {
        return cd.depreciationPeriodMonths;
      }
      return "—";
    },
    providerId() {
      const p = this.depense && this.depense.provider;
      if (!p) return null;
      return typeof p === "object" && p._id ? p._id : p;
    },
  },
  methods: {
    attachmentDisplayName(filePath) {
      if (!filePath) return "—";
      const chunks = String(filePath).split("/");
      return chunks[chunks.length - 1] || filePath;
    },
    resourceUrl(filePath) {
      if (!filePath) return "";
      const tpl = this.settings && this.settings.resourcesUrl;
      return tpl ? tpl.replace("FILE_PATH", filePath) : "";
    },
    formatDate(val) {
      return val ? moment(val).format("DD/MM/YYYY") : "—";
    },
    formatDateTime(val) {
      return val ? moment(val).format("DD/MM/YYYY HH:mm") : "—";
    },
    invoiceStatusColor(status) {
      if (status === "paid") return "green";
      if (status === "partial") return "orange";
      return "red";
    },
    invoiceStatusLabel(status) {
      if (status === "paid") return this.$t("fournisseur.paid");
      if (status === "partial") return this.$t("fournisseur.partial");
      return this.$t("fournisseur.unpaid");
    },
  },
};
</script>

<style>
@import "~@/views/compV3/common.css";

.attachment-links-list a {
  word-break: break-all;
}
.attachment-grid {
  display: grid;
  gap: 8px;
}
.attachment-item {
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: #fafafa;
}
.attachment-main {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}
.attachment-icon {
  color: #1890ff;
}
.attachment-name {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.attachment-action {
  white-space: nowrap;
}
</style>
